import { useQuery } from "@tanstack/react-query"
import {
  CalendarClock,
  CalendarDays,
  FileCheck,
  FilePen,
  FileStack,
  FileWarning,
  Gift,
  HandCoins,
  IdCard,
  Landmark,
  Plane,
  ShieldCheck,
  UserMinus,
  UserRoundX,
  Users,
  Wallet,
} from "lucide-react"

import { api } from "@/api/cliente"
import {
  type ContratoNominaMin,
  type DocumentoTHMin,
  type EventoMin,
  kpiAgenda,
  kpiContratos,
  kpiDocumentosTH,
  kpiPlantillas,
  type PlantillaMin,
} from "@/componentes/kpi/calculos"
import { FilaKpi } from "@/componentes/kpi/FilaKpi"
import { TarjetaKpi } from "@/componentes/ui/TarjetaKpi"
import { hoyLocal } from "@/lib/fechas"

/**
 * Los indicadores de las pestañas de Talento humano.
 *
 * Cada fila pide sus datos con la MISMA clave que su pestaña: TanStack Query comparte la
 * caché, así que no hay un viaje de más y lo que la lista refresca, los indicadores
 * también. Personas es la excepción: se pagina, y se cuenta en el servidor.
 */

const plural = (n: number, uno: string, varios: string) => `${n} ${n === 1 ? uno : varios}`

type IndicadoresPersonal = {
  activos: number
  directiva: number
  suspendidos: number
  egresados: number
  contactos: number
  sin_fecha_ingreso: number
  sin_contrato: number
  incompletos: number
  sin_cuenta: number
}

export function KpiPersonas() {
  const q = useQuery({
    queryKey: ["personas", "indicadores"],
    queryFn: () => api.get<IndicadoresPersonal>("/api/v1/personas/indicadores"),
  })
  const k = q.data
  return (
    <FilaKpi cargando={q.isPending} error={q.isError} className="mb-4">
      {k && (
        <>
          <TarjetaKpi
            titulo="Personal activo"
            detalle="Empleados y directiva"
            Icono={Users}
            color="var(--primary)"
            cantidad={k.activos}
            pie={plural(k.directiva, "de la directiva", "de la directiva")}
          />
          <TarjetaKpi
            titulo="Expediente incompleto"
            detalle="Sin fecha de ingreso o sin contrato"
            Icono={FileWarning}
            color="var(--warning)"
            cantidad={k.incompletos}
            pie={`${k.sin_fecha_ingreso} sin ingreso · ${k.sin_contrato} sin contrato vigente`}
          />
          <TarjetaKpi
            titulo="Sin acceso al sistema"
            detalle="Personal activo sin cuenta"
            Icono={UserRoundX}
            color="var(--info)"
            cantidad={k.sin_cuenta}
          />
          <TarjetaKpi
            titulo="Egresados y suspendidos"
            detalle="Conservan su expediente"
            Icono={UserMinus}
            color="var(--fg-muted)"
            cantidad={k.egresados + k.suspendidos}
            pie={`${k.egresados} egresados · ${k.suspendidos} suspendidos · ${plural(k.contactos, "contacto de cliente", "contactos de clientes")}`}
          />
        </>
      )}
    </FilaKpi>
  )
}

export function KpiContratos() {
  const q = useQuery({
    queryKey: ["nomina-contratos"],
    queryFn: () => api.get<ContratoNominaMin[]>("/api/v1/nomina/contratos"),
  })
  const k = q.data ? kpiContratos(q.data, hoyLocal()) : null
  return (
    <FilaKpi cargando={q.isPending} error={q.isError}>
      {k && (
        <>
          <TarjetaKpi
            titulo="Contratos vivos"
            detalle="Quién cobra hoy por nómina"
            Icono={IdCard}
            color="var(--primary)"
            cantidad={k.vivos}
            pie={`${k.semanales} semanales · ${k.quincenales} quincenales · ${k.mensuales} mensuales`}
          />
          <TarjetaKpi
            titulo="Sueldo por pago"
            detalle="Lo de cada pago, no del mes"
            Icono={Wallet}
            color="var(--info)"
            cantidad={k.vivos}
            montos={k.porPago}
          />
          <TarjetaKpi
            titulo="Vencen en 30 días"
            detalle="Contratos con fecha de fin"
            Icono={CalendarClock}
            color="var(--warning)"
            cantidad={k.vencen}
          />
          <TarjetaKpi
            titulo="Sin conceptos"
            detalle="Sólo el sueldo base"
            Icono={HandCoins}
            color="var(--fg-muted)"
            cantidad={k.sinConceptos}
          />
        </>
      )}
    </FilaKpi>
  )
}

export function KpiDocumentosTH() {
  // Sin filtro de estado, aunque la lista arranque en «solicitados»: un indicador que
  // cambiara con el filtro de abajo no diría cuánto hay, diría cuánto se está mirando.
  const q = useQuery({
    queryKey: ["documentos-th", "lista", ""],
    queryFn: () => api.get<DocumentoTHMin[]>("/api/v1/documentos-th"),
  })
  const k = q.data ? kpiDocumentosTH(q.data, hoyLocal()) : null
  return (
    <FilaKpi cargando={q.isPending} error={q.isError} className="mb-4">
      {k && (
        <>
          <TarjetaKpi
            titulo="Por resolver"
            detalle="Solicitudes del personal"
            Icono={FilePen}
            color="var(--warning)"
            cantidad={k.porResolver}
          />
          <TarjetaKpi
            titulo="Emitidos este mes"
            detalle="Documentos generados"
            Icono={FileCheck}
            color="var(--success)"
            cantidad={k.emitidosMes}
          />
          <TarjetaKpi
            titulo="Sin firmado"
            detalle="Emitidos sin el escaneado firmado"
            Icono={FileWarning}
            color="var(--info)"
            cantidad={k.sinFirmado}
          />
          <TarjetaKpi
            titulo="Anulados o rechazados"
            detalle="Conservan su rastro"
            Icono={FileStack}
            color="var(--fg-muted)"
            cantidad={k.anulados}
          />
        </>
      )}
    </FilaKpi>
  )
}

/** La agenda ya la trae la pestaña, con el horizonte elegido: se cuenta lo que se ve. */
export function KpiAgenda({ eventos, dias }: { eventos: readonly EventoMin[]; dias: number }) {
  const k = kpiAgenda(eventos)
  const pie = `En los próximos ${dias} días`
  return (
    <FilaKpi cargando={false} error={false}>
      <TarjetaKpi
        titulo="Salen de vacaciones"
        detalle="Vacaciones programadas que empiezan"
        Icono={Plane}
        color="var(--info)"
        cantidad={k.salen}
        pie={pie}
      />
      <TarjetaKpi
        titulo="Nacen períodos"
        detalle="Aniversarios: nuevo derecho a vacaciones"
        Icono={CalendarDays}
        color="var(--warning)"
        cantidad={k.nacen}
        pie={pie}
      />
      <TarjetaKpi
        titulo="Pagos de utilidades"
        detalle="Programados"
        Icono={Gift}
        color="var(--success)"
        cantidad={k.utilidades}
        pie={pie}
      />
      <TarjetaKpi
        titulo="Depósitos de garantía"
        detalle="Trimestres de servicio cumplidos"
        Icono={ShieldCheck}
        color="var(--primary)"
        cantidad={k.garantias}
        pie={`${plural(k.personas, "persona", "personas")} con algo en la agenda`}
      />
    </FilaKpi>
  )
}

export function KpiPlantillas() {
  const q = useQuery({
    queryKey: ["plantillas-th", "todas"],
    queryFn: () => api.get<PlantillaMin[]>("/api/v1/plantillas-th"),
  })
  const k = q.data ? kpiPlantillas(q.data) : null
  return (
    <FilaKpi cargando={q.isPending} error={q.isError} className="mb-4">
      {k && (
        <>
          <TarjetaKpi
            titulo="Plantillas activas"
            detalle="Documentos que se pueden generar"
            Icono={FileStack}
            color="var(--primary)"
            cantidad={k.activas}
          />
          <TarjetaKpi
            titulo="De autoservicio"
            detalle="Las pide el propio empleado"
            Icono={Users}
            color="var(--info)"
            cantidad={k.autoservicio}
          />
          <TarjetaKpi
            titulo="Con aprobación"
            detalle="Talento Humano las resuelve"
            Icono={Landmark}
            color="var(--warning)"
            cantidad={k.conAprobacion}
          />
          <TarjetaKpi
            titulo="Archivadas"
            detalle="Ya no se ofrecen"
            Icono={FileWarning}
            color="var(--fg-muted)"
            cantidad={k.archivadas}
          />
        </>
      )}
    </FilaKpi>
  )
}
